import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, useTheme } from '@mui/styles';
import { toast } from 'react-toastify';

import PropTypes from 'prop-types';
import ReactJSON from 'react-json-view';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Input from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import TransformIcon from '@mui/icons-material/Transform';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import HuntIcon from '@mui/icons-material/TrackChanges';
import SaveIcon from '@mui/icons-material/Save';
import Favorite from '@mui/icons-material/Favorite';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import NavigateBeforeIcon from '@mui/icons-material/KeyboardArrowUp';
import NavigateNextIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import TuneIcon from '@mui/icons-material/Tune';
import FindInPageIcon from '@mui/icons-material/FindInPage';

import StyledPaper from '../../../components/styled/paper';
import Tooltip from '../../../components/styled/tooltip';
import KeyValuePair from '../../../components/keyvaluepair';
import Hexdump from '../../../components/hexdump';
import SensorName from '../../../components/sensorname';
import BTJobName from '../../../components/bt-jobs';
import ProgressBar from '../../../components/progress-bar';
import TabPanel from '../../../components/tabpanel';
import { TagSimpleList } from '../../../components/tags/taglist';
import Tag from '../../../components/tags/tag';
import ConfirmationDialog from '../../../components/confirmationdlg';
import PopupMenu from '../../../components/popupmenu';
import LabeledToolbar from '../../../components/styled/toolbar';

// our IsEmpty is a truthiness test
import { Flatten, SaveFile, ToArray, IsEmpty, GetBTUUID } from '../../../libraries/helpers';
import useWindowSize from '../../../libraries/usewindowsize';

import AlertsService from '../../../data/alerts-service';
import SuricataService from '../../../data/suricata-service';
import Auth, { User, ProfileContext, ParamsSerializer } from '../../../data/auth-service';
import SensorsService from '../../../data/sensors-service.js';
import DataContext from '../../../data/data-context.js';
import PolicyService from '../../../data/policy-service.js';
import PcapService from '../../../data/pcap-service.js';
import BacktestingService from '../../../data/backtesting-service.js';

import MLResults from './ml-results';

import Moment from 'moment';
import {
    get as _get,
    set as _set,
    merge as _merge,
    isEmpty as _isEmpty, // collections test
} from 'lodash';

import { UnControlled as CodeMirror } from 'react-codemirror2';

const useStyles = makeStyles(theme => ({
    root: {
        width: 'calc(100% - 10px)',
        padding: 5,
        position: 'absolute',
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],

        '& .CodeMirror-scroll': {
            maxHeight: 600,
        },

        '& .react-json-view': {
            paddingBottom: 50,
        },
    },
    alertHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        paddingRight: 0,
    },
    tagsToolbar: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        direction: 'rtl',
        paddingTop: 16,
    },
    box: {
        overflowY: 'scroll',
        msOverflowStyle: 'none',
        '& code': {
            fontSize: '15px',
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    float: {
        float: 'right',
        position: 'relative',
        zIndex: '10000',
    },
    rawJson: {
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
    },
    justifySpace: {
        justifyContent: 'space-between',
    },
    tabToolbar: {
        justifyContent: 'flex-end',
    },
    autoColumnList: {
        overflow: 'auto',
        paddingTop: 0,
        paddingBottom: 0,

        '& ul': {
            columns: '400px auto',
            listStyle: 'none',
            padding: 0,
            marginTop: 0,
            marginBottom: 0,

            '& li': {
                padding: 0,
                height: 'fitContent',
                breakInside: 'avoid',
            }
        },
        // nope: can't save people from themselves
        // hide ugly windows scrollbar
        // https://stackoverflow.com/a/38994837/5895550
        // '-ms-overflow-style': 'none', /* Internet Explorer 10+ */
        // scrollbarWidth: 'none', /* Firefox */
        //
        // '&::-webkit-scrollbar': {
        //     display: 'none', /* Safari and Chrome */
        // }
    },
    inlineTag: {
        display: 'inline-block',
        verticalAlign: 'middle',
        direction: 'ltr', // don't reverse the popups
    },
    margin: {
        margin: theme.spacing(1),
    },
    itemIcon: {
        minWidth: 25,
    },
    signature: {
        ...theme.typography.h6,
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    orange: {
        color: theme.palette.secondary.main,
        paddingLeft: 5,
    },
    mapped: {
        verticalAlign: 'middle',
        paddingRight: 5,
    }
}));

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

const otherFields = {
    'm.SHA256': {
        variable: 'SHA256',
        field: 'm.SHA256',
        indexed: false,
        title: 'SHA256',
        type: 'file_hash',
    },
    'data.ml_server.SHA256': {
        variable: 'data.ml_server.SHA256',
        field: 'data.ml_server.SHA256',
        indexed: false,
        title: 'SHA256',
        type: 'file_hash',
    },
};

const AlertDetail = (props) => {
    const user = User().username;
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { height } = useWindowSize();

    const { updateProfile, getProfile, /*perm*/ } = useContext(ProfileContext);
    const {
        sensorNames,
        alertMappings: mappings,
        backtestingJobs,
    } = useContext(DataContext);
    const toolbarRef = useRef(null);

    const alertPerms = Auth.permissions.get('Alerts');
    const tagPerms = Auth.permissions.get('Tags');
    const sensorPerms = Auth.permissions.get('Sensors');
    const suriPerms = Auth.permissions.get('SuricataRulesPolicy');
    const gatorPerms = Auth.permissions.get('DataTuningPolicy');
    const pcapPerms = Auth.permissions.get('Pcap');

    const [fields, setFields] = useState({});
    const [showFavorites, setShowFavorites] = useState(false);
    const [favorites, setFavorites] = useState({});
    const [jsonFormatted, setJSONFormatted] = useState(true);
    const [rule, setRule] = useState();
    const [metadata, setMetadata] = useState();
    const [metadataLoaded, setMetadataLoaded] = useState(false);
    const [dpid, setDpid] = useState();
    const [backtesting, setBacktesting] = useState();
    const [sandboxReport, setSandboxReport] = useState();
    const [flatAlert, setFlatAlert] = useState({});
    const [isread, setIsread] = useState(false);
    const [malware, setMalware] = useState();
    const [note, setNote] = useState('');
    const [policy, setPolicy] = useState({});
    const [cheight, setCHeight] = useState(200);
    const [search, setSearch] = useState('');
    const [showTags, setShowTags] = useState(false);
    const [tab, setTab] = useState(0);
    const [valuePopup, setValuePopup] = useState();
    const [partialDownloadConfirm, setPartialDownloadConfirm] = useState(false);
    const [pcapSituation, setPcapSituation] = useState({});
    const [synonyms, setSynonyms] = useState({});

    const [tabs, setTabs] = useState({
        Alert: { show: true, order: 0 },
        Rule: { order: 1 },
        'ML Model Results': { order: 2 },
        Malware: { order: 3 },
        Metadata: { order: 4 },
        Backtesting: { order: 5 },
        Payload: { order: 6 },
        Packet: { order: 7 },
        'Sandbox Report': { order: 8 },
        'Raw JSON': { show: true, order: 9 },
        DPID:{ order: 10 },
    });

    const field = k => {
        return AlertsService.fieldsMap[k] ||
        AlertsService.fieldsMap[`data.${k}`] ||
        otherFields[k] ||
        {};
    };

    const skip = path => fields[path] &&
        !(fields[path].btShow) &&
        fields[path].jsonHidden;

    const getSort = path => fields[path] ? fields[path].jsonSort || 1111 : 1111;

    const isFavorite = (path) => favorites[path];

    const handleJSONFormat = () => {
        setJSONFormatted(!jsonFormatted);
    };

    const handleSaveJson = () => {
        const json = { alert: props.alert };
        if (rule) json.rule = rule;
        if (metadata) json.metadata = metadata;
        if (sandboxReport) json.sandboxReport = sandboxReport;
        const sensor = sensorNames[_get(props.alert, 'data.bricata.sensor_uuid')];
        if (sensor) json.sensor = sensor.name || sensor.host;
        delete json.count;
        SaveFile(JSON.stringify(json), `${flatAlert.uuid}.json`, 'text/json;charset=utf-8;');
    };

    const handleToggleFavorite = (path) => {
        const profile = getProfile('alerts');
        const f = favorites;
        if (f[path]) delete f[path];
        else f[path] = true;

        setFavorites({ ...f });
        updateProfile('alerts', { ...profile, favorites: { show: showFavorites, selections: f } });
    };

    const handleNoteChanged = event => {
        setNote(event.target.value || '');
    };

    const handleSearchChanged = event => {
        setSearch(event.target.value || '');
    };

    const handleSaveNote = () => {
        props.onSaveNote && props.onSaveNote(flatAlert.uuid, note);
        flatAlert['data.bricata.note'] = note;
    };

    const handleShowFaboritesToggle = () => {
        setShowFavorites(!showFavorites);
        const profile = getProfile('alerts');
        updateProfile('alerts', _set(profile, 'favorites.show', !showFavorites));
    };

    const handleDownloadSandboxReport = e => {
        // alert, task, sensor, type, url
        e && e.data && AlertsService.downloadSandboxReport({
            alert: props.alert.uuid,
            task: e.data.task_id,
            type: e.data.type,
            url: e.data.url,
            sensor: flatAlert['data.bricata.sensor_uuid'],
        });
    };

    const handleDownloadMalware = () => {
        // partial file warning
        if (flatAlert['data.bro.missing_bytes']) {
            setPartialDownloadConfirm(true);
            return;
        }
        doDownloadMalware();
    };

    const doDownloadMalware = () => {
        malware && AlertsService.downloadMalwareFile({
            uuid: props.alert.uuid,
            sensor: flatAlert['data.bricata.sensor_uuid'],
            sha256: malware['m.SHA256'],
            md5: flatAlert['data.bro.md5'],
            cmc: props.cmc,
        });
    };

    const buildHuntFitler = useCallback(type => {
        switch (type) {
            case 'By Flow UUID':
                return {
                    operator: 'Or',
                    nodes: [{ variable: 'bricata.flow_uuid', value: flatAlert['data.bricata.flow_uuid'] }]
                };
            case 'By source IP':
                return {
                    operator: 'Or',
                    nodes: [
                        { variable: 'bro_log.id.orig_h', value: flatAlert['data.src_ip'] },
                        { variable: 'bro_log.id.resp_h', value: flatAlert['data.src_ip'] }]
                };
            case 'By dest IP':
                return {
                    operator: 'Or',
                    nodes: [
                        { variable: 'bro_log.id.orig_h', value: flatAlert['data.dest_ip'] },
                        { variable: 'bro_log.id.resp_h', value: flatAlert['data.dest_ip'] }]
                };
            default:
                return {
                    operator: 'Or',
                    nodes: [
                        {
                            operator: 'And',
                            nodes: [
                                { variable: 'bro_log.id.resp_h', value: flatAlert['data.dest_ip'] },
                                { variable: 'bro_log.id.orig_h', value: flatAlert['data.src_ip'] }]
                        },
                        {
                            operator: 'And',
                            nodes: [
                                { variable: 'bro_log.id.orig_h', value: flatAlert['data.dest_ip'] },
                                { variable: 'bro_log.id.resp_h', value: flatAlert['data.src_ip'] }]
                        }]
                };
        }
    }, [flatAlert]);

    const handleHunt = type => {
        const end = type === 'By Flow UUID'
            ? Moment(props.alert.timestamp).add(24, 'hour')
            : Moment(props.alert.timestamp).add(42, 'minute');
        const filter = buildHuntFitler(type);
        const p = {
            start: Moment(props.alert.timestamp).subtract(42, 'minute').utc().toISOString(),
            end: Moment().isBefore(end) ? undefined : end.utc().toISOString(),
            filter: JSON.stringify(filter),
            cmc: (props.cmc || {}).uuid,
        };
        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    };

    const getPcapMenu = useCallback(() => {
        const menu = [];
        if (pcapSituation.full) menu.push('Request full Pcap');
        if (pcapSituation.smart) menu.push('Request Smart Pcap');
        return menu;
    }, [pcapSituation]);

    const handlePcap = type => {
        const sensor = sensorNames[_get(props.alert, 'data.bricata.sensor_uuid')];
        const host = sensor ? sensor.host : _get(props.alert, 'data.bricata.sensor_ipv4');

        const params = PcapService.alert2filter(props.alert);
        if (type === 'Request Smart Pcap') {
            PcapService.create(host, params, props.cmc)
                .then(() => {
                    const msg = MC2CHECK
                        ? 'Check the PCAP Request page for the results'
                        : 'You will be notified when your PCAP is ready';
                    toast.info(msg, { hideProgressBar: true });
                }, err => {
                    console.error(err);
                    toast.error('There was an error issuing a PCAP request', { hideProgressBar: true });
                });
            return;
        }
        navigate(`/views/packet_requests/create_pcap?${ParamsSerializer({ ...params, host })}`);
    };

    const canPolicy = type => {
        if (!policy || !policy[type]) return false;
        return MC2CHECK ? policy[type].endsWith('.mc2') : !policy[type].endsWith('.mc2');
    };

    const handleGoToSuricy = () => {
        const p = {
            group: _get(rule, 'rule.group'),
            search: _get(rule, 'rule.id'),
        };
        navigate(`/policies/suricata/rules/${policy.suricy}?${ParamsSerializer(p)}`);
    };

    const handleTune = async () => {
        if (!policy.gator) return;

        const nodes = [
            {
                ...AlertsService.fieldsMap['data.src_ip'],
                value: flatAlert['data.src_ip'],
            },
            {
                ...AlertsService.fieldsMap['data.dest_ip'],
                value: flatAlert['data.dest_ip']
            },
            {
                ...AlertsService.fieldsMap['data.alert.severity'],
                value: `${flatAlert['data.alert.severity']}`,
            },
            {
                ...AlertsService.fieldsMap['data.alert.signature_id'],
                value: `${flatAlert['data.alert.signature_id']}`
            },
            // {
            //     ...AlertsService.fieldsMap['data.alert.category'],
            //     value: flatAlert['data.alert.category'],
            // }
        ];
        // don't add empty ports
        if (flatAlert['data.dest_port']) {
            nodes.splice(2, 0, {
                ...AlertsService.fieldsMap['data.dest_port'],
                value: `${flatAlert['data.dest_port']}`
            });
        }
        if (flatAlert['data.src_port']) {
            nodes.splice(2, 0, {
                ...AlertsService.fieldsMap['data.src_port'],
                value: `${flatAlert['data.src_port']}`
            });
        }

        props.onTune({ nodes }, policy.gator);
    };

    const handleMarkRead = () => {
        props.onReadAlert && props.onReadAlert(props.alert, !isread);

        // update data immediately
        if (!flatAlert['data.bricata.read']) flatAlert['data.bricata.read'] = [];
        const i = flatAlert['data.bricata.read'].findIndex(u => u === user);
        if (isread && i >= 0) {
            flatAlert['data.bricata.read'].splice(i, 1);
        } else if (i < 0) {
            flatAlert['data.bricata.read'].unshift(user);
        }
        setFlatAlert(flatAlert);
        setIsread(!isread);
    };

    const getTabIndex = useCallback(t => {
        const tl = (Object.keys(tabs) || [])
            .filter(t => tabs[t].show)
            .sort((a, b) => tabs[a].order - tabs[b].order);

        return tl.findIndex(tab => tab === t);
    }, [tabs]);

    useEffect(() => {
        const visibleTabs = Object.keys(tabs || {})
            .filter(t => tabs[t].show)
            .sort((a, b) => tabs[a].order - tabs[b].order);
        
        const maxValidTabIndex = visibleTabs.length - 1;
        // If current tab index is greater than the maximum valid index, reset to last tab
        if (tab > maxValidTabIndex && maxValidTabIndex >= 0) {
            setTab(maxValidTabIndex);
        }
    }, [tabs, tab]);

    const handleTabChanged = (e, v) => setTab(v);

    const handleAddTag = t => {
        props.onTag && props.onTag(props.alert, t);
        // update visible rows immediately
        const d = props.alert;
        if (!_get(d, 'data.bricata.tag')) _set(d, 'data.bricata.tag', []);
        if (!d.data.bricata.tag.find(tag => tag === t.name)) d.data.bricata.tag.unshift(t.name);
        setFlatAlert(Flatten({ object: d, delimiter: '.' }));
    };

    const handleUntag = t => {
        props.onUntag && props.onUntag(props.alert, t);
        // update visible rows immediately
        const d = props.alert;
        if (!_get(d, 'data.bricata.tag')) return;
        const i = (d.data.bricata.tag || []).findIndex(tag => t === tag);
        if (i >= 0) d.data.bricata.tag.splice(i, 1);
        setFlatAlert(Flatten({ object: d, delimiter: '.' }));
    };

    const isIndexed = k => {
        const f = field(k);
        return f && f.type !== 'date' && f.indexed;
    };

    const getType = k => {
        const f = field(k);
        return (f || {}).type;
    };

    const getInfo = k => {
        const f = field(k);
        return (f || {}).info;
    };

    const isIP = k => {
        const f = field(k);
        return (f || {}).type === 'ip';
    };

    const isFileHash = k => {
        const f = field(k);
        return (f || {}).type === 'file_hash';
    };

    const getCountry = k => {
        if (k === 'data.src_ip') return flatAlert['data.bricata.src_location.country'];
        if (k === 'data.dest_ip') return flatAlert['data.bricata.dest_location.country'];
    };

    const getTitle = useCallback(k => {
        const f = synonyms[k] || field(k);
        return (f || {}).title;
    }, [synonyms]);

    // get's mapped value
    const getValue = useCallback((k, v) => {
        const f = field(k);
        const m = mappings[f.variable];
        if (!m) return v;

        let ret = m.reduce((a, c) => {
            const cv = AlertsService.value(props.alert, AlertsService.columnsMap[c]);
            return [
                ...a,
                ...ToArray(cv)
            ];
        }, []).filter(a => !IsEmpty(a, true));
        ret = ret.length === 1 ? ret[0] : ret;
        return ret;
    }, [mappings, props.alert]);

    // returns tip for mapped columns, single value or array
    const getMappedTip = useCallback((k) => {
        const f = field(k);
        const m = mappings[f.variable];
        if (!m) return '';

        let ret = m.reduce((a, c) => {
            const cv = AlertsService.value(props.alert, AlertsService.columnsMap[c]);
            return [
                ...a,
                ...ToArray(cv).map(value => ({ key: getTitle(c), value }))
            ];
        }, []).filter(a => !IsEmpty(a.value, true)).map(a => a.key);
        ret = ret.length === 1 ? ret[0] : ret;
        return ret;
    }, [props.alert, mappings, getTitle]);

    const getTitleIcon = useCallback(k => {
        const f = field(k);
        if (mappings[f.variable] && (mappings[f.variable].length > 1 || mappings[f.variable][0] !== f.variable)) {
            const ms = mappings[f.variable].map(m => `${AlertsService.columnsMap[m].title}`);
            return (<Tooltip
                arrow placement="top"
                size="small"
                title={ms.join(', ')}>
                <TransformIcon color='primary' className={classes.mapped} size='small'/>
            </Tooltip>);
        }
        return undefined;
    }, [mappings, classes]);

    const addSynonym = (k, delimiter, depth) => {
        const a = k.split(delimiter);
        if (a.length > 1) {
            const dk = a.slice(depth - 1).join('-');
            if (synonyms[dk]) {
                console.log('we need to go deeper', k, synonyms);
                return addSynonym(k, delimiter, depth - 1);
            }
            setSynonyms(current => ({
                ...current,
                [k]: { title: dk }
            }));
        }
    };

    const disambiguate = ({ object, delimiter }) => {
        const flat = Flatten({ object, delimiter });
        const m = {};
        const keys = Object.keys(flat).filter(k => !skip(k) && !getTitle(k));
        keys.forEach((k, i) => {
            const title = (k || '').split(delimiter).slice(-1).pop();
            if (m[title]) {
                addSynonym(k, delimiter, -1);
                const og = keys.find(kk => (kk || '')
                    .split(delimiter)
                    .slice(-1).pop() === title);
                addSynonym(og, delimiter, -1);
            }
            m[title] = true;
        });
    };

    const handleOnValueClick = (e, variable, value) => {
        setValuePopup({ elm: e, variable, value });
    };

    const handleOnFilter = e => {
        if (!props.onFilter || !valuePopup) return;
        const f = field(valuePopup.variable);
        f && props.onFilter([{
            variable: f.variable,
            value: valuePopup.value,
            type: f.type
        }]);
        setValuePopup();
    };

    const renderTab = (k) => {
        switch (k) {
            case 'Alert': return (
                <React.Fragment>
                    <Box p={1} display="flex" style={{ paddingBottom: 0 }}>
                        <Box p={1} flexGrow={1}>
                            {!props.onFilter && <Typography className={classes.signature} variant="h6">{flatAlert['data.alert.signature']}</Typography>}

                            {props.onFilter && flatAlert['data.alert.signature'] && <Link
                                color="primary"
                                onClick={e => handleOnValueClick(e.currentTarget, 'data.alert.signature', flatAlert['data.alert.signature'])}
                                className={classes.signature}>
                                {flatAlert['data.alert.signature']}
                            </Link>}
                        </Box>
                        <Box p={1}>
                            <Input
                                disabled={!alertPerms.write}
                                value={note}
                                onChange={handleNoteChanged}
                                label="Note"
                                aria-label="note"
                                variant="outlined"
                                margin="dense"
                                style={{ margin: 0 }}
                                InputProps={alertPerms.write && {
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            disabled={note === (flatAlert['data.bricata.note'] || '')}
                                            aria-label="save note"
                                            onClick={handleSaveNote}
                                            size="small">
                                            <SaveIcon fontSize="inherit" />
                                        </IconButton>
                                    </InputAdornment>
                                }}
                            />
                        </Box>
                        <Box p={1}>
                            <Input
                                value={search}
                                onChange={handleSearchChanged}
                                placeholder="Search..."
                                variant="outlined"
                                margin="dense"
                                style={{ margin: 0 }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">
                                        <React.Fragment>
                                            <IconButton
                                                disabled={!search}
                                                aria-label="clear"
                                                title="clear"
                                                onClick={() => setSearch('')}
                                                size="large">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                            <SearchIcon fontSize="inherit" />
                                        </React.Fragment>
                                    </InputAdornment>
                                }}
                            />
                        </Box>
                        <Box p={1} xs={2} style={{ paddingLeft: 25 }}>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={showFavorites}
                                    onChange={handleShowFaboritesToggle}
                                    icon={<FavoriteBorder />}
                                    checkedIcon={<Favorite />} name="favorites" />}
                                label="Favorites"
                            />
                        </Box>
                    </Box>
                    <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                        <ul>
                            {Object.entries(flatAlert || {})
                                .filter(([k]) =>
                                    !skip(k) &&
                                    (!showFavorites || isFavorite(k)) &&
                                    (search.length < 1 ||
                                     k.toLowerCase().includes(search.toLowerCase()) ||
                                     (getTitle(k) || '').toLowerCase().includes(search.toLowerCase())))
                                .sort((a, b) => getSort(a[0]) - getSort(b[0]))
                                .map(([k, v], i) =>
                                    <li key={`${k}_${i}`}>
                                        <KeyValuePair
                                            path={k}
                                            data={getValue(k, v)}
                                            title={getTitle(k)}
                                            icon={getTitleIcon(k)}
                                            tip={getMappedTip(k)}
                                            type={getType(k)}
                                            indexed={isIndexed(k)}
                                            info={getInfo(k)}
                                            ip={isIP(k)}
                                            fileHash={isFileHash(k)}
                                            country={getCountry(k)}
                                            sensor={flatAlert['data.bricata.sensor_uuid']}
                                            favorite={isFavorite(k)}
                                            onFavorite={() => handleToggleFavorite(k)}
                                            onClick={isIndexed(k) && props.onFilter
                                                ? (e, v) => handleOnValueClick(e, k, v)
                                                : undefined
                                            }/>
                                    </li>
                                )}
                        </ul>
                    </Box>
                </React.Fragment>
            );
            case 'Rule': return (
                <React.Fragment>
                    <Box p={1} display="flex" style={{ paddingBottom: 0 }}>
                        <Box p={1} flexGrow={1}>
                            <Typography variant="h6">{flatAlert['data.alert.signature']}</Typography>
                        </Box>
                        <Box p={1} xs={2}>
                            Policy:
                            {canPolicy('suricy') && policy.suricy && <Link
                                style={{ marginLeft: 10 }}
                                component="button"
                                variant="button"
                                aria-label="policy link"
                                onClick={handleGoToSuricy}>
                                {policy.suricy}
                            </Link>}
                            {!canPolicy('suricy') &&
                                <span className={classes.orange}>{policy.suricy}</span>
                            }
                        </Box>
                    </Box>
                    <Box style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                        <Box p={1}>
                            <CodeMirror
                                value={(rule['rule.options'] || '').replace(/;\s*/g, ';\n')}
                                options={{
                                    mode: 'suricata',
                                    theme: theme.codeMirror.theme,
                                    lineNumbers: true,
                                    lineWrapping: true,
                                    readOnly: true,
                                }}
                                onChange={(editor, data, value) => {
                                }}
                            />
                        </Box>
                        <Box p={1}>
                            <ul>
                                {Object.entries(rule || {}).filter(([k]) => !skip(k)).map(([k, v], i) =>
                                    <li key={`${k}_${i}`}>
                                        <KeyValuePair
                                            path={k}
                                            data={v}/>
                                    </li>
                                )}
                            </ul>
                        </Box>
                    </Box>
                </React.Fragment>
            );
            case 'Malware': return (
                <React.Fragment>
                    <Box p={1} display="flex" style={{ paddingBottom: 0 }} flexDirection="row-reverse">
                        <Box p={1} xs={2}>
                            <Button
                                variant="outlined"
                                size="small"
                                aria-label={flatAlert['data.bro.missing_bytes'] ? 'Partial Malware' : 'Malware'}
                                startIcon={<SaveAltIcon fontSize={'inherit'} />}
                                onClick={handleDownloadMalware}>
                                {flatAlert['data.bro.missing_bytes'] ? 'Partial Malware' : 'Malware'}
                            </Button>
                        </Box>
                    </Box>
                    <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                        <ul>
                            {Object.entries(malware || {}).filter(([k]) => !skip(k)).map(([k, v], i) =>
                                <li key={`${k}_${i}`}>
                                    <KeyValuePair
                                        title={getTitle(k)}
                                        type={getType(k)}
                                        fileHash={isFileHash(k)}
                                        path={k}
                                        data={v}/>
                                </li>
                            )}
                        </ul>
                    </Box>
                </React.Fragment>
            );
            case 'Metadata': return (
                <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                    {!metadataLoaded ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                            <Typography>Loading metadata...</Typography>
                        </Box>
                    ) : (
                        <ul>
                            {Object.entries(metadata || {}).filter(([k]) => !skip(k)).map(([k, v], i) =>
                                <li key={`${k}_${i}`}>
                                    <KeyValuePair
                                        title={getTitle(k)}
                                        type={getType(k)}
                                        fileHash={isFileHash(k)}
                                        path={k}
                                        data={v}/>
                                </li>
                            )}
                        </ul>
                    )}
                </Box>
            );
            case 'DPID': return (
                <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                    {!metadataLoaded ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                            <Typography>Loading DPID data...</Typography>
                        </Box>
                    ) : (
                        <ul>
                            {Object.entries(dpid || {}).filter(([k]) => !skip(k)).map(([k, v], i) =>
                                <li key={`${k}_${i}`}>
                                    <KeyValuePair
                                        title={getTitle(k)}
                                        type={getType(k)}
                                        fileHash={isFileHash(k)}
                                        path={k}
                                        data={v}/>
                                </li>
                            )}
                        </ul>
                    )}
                </Box>
            );
            case 'Backtesting': return (
                <React.Fragment>
                    <Box p={1} display="flex" style={{ paddingBottom: 0 }}>
                        {/* job name */}
                        {!props.onFilter && <Typography className={classes.signature} variant="h6">
                            <BTJobName uuid={flatAlert['data.bt_uuid']}/>
                        </Typography>}

                        {props.onFilter && flatAlert['data.bt_uuid'] && <Link
                            color="primary"
                            onClick={e => handleOnValueClick(e.currentTarget, 'data.bt_uuid', flatAlert['data.bt_uuid'])}
                            className={classes.signature}>
                            <BTJobName uuid={flatAlert['data.bt_uuid']}/>
                        </Link>}

                        {/* jobs progress */}
                        {backtesting['backtesting.data.progress'] > 0 &&
                            <div style={{ width: 250, marginLeft: 20 }}>
                                <ProgressBar value={backtesting['backtesting.data.progress']}/>
                            </div>
                        }
                    </Box>

                    {/* file name */}
                    {_get(flatAlert, 'data.bricata.bt_pcap_name') &&
                        <Box style={{ marginBottom: 15 }}>
                            <KeyValuePair
                                title="PCAP File Name"
                                type="str"
                                path={'bricata.bt_pcap_name'}
                                data={_get(flatAlert, 'data.bricata.bt_pcap_name')}/>
                        </Box>
                    }

                    <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                        <ul>
                            {Object.entries(backtesting || {})
                                .filter(([k]) => !skip(k))
                                .map(([k, v], i) =>
                                    <li key={`${k}_${i}`}>
                                        <KeyValuePair
                                            title={getTitle(k)}
                                            type={getType(k)}
                                            fileHash={isFileHash(k)}
                                            path={k}
                                            data={v}/>
                                    </li>
                                )}
                        </ul>
                    </Box>
                </React.Fragment>
            );
            case 'Packet': return (
                <Hexdump data={flatAlert['data.packet']} columns={4} columnBytes={4} />
            );
            case 'Payload': return (
                <Hexdump data={flatAlert['data.payload']} columns={4} columnBytes={4} />
            );
            case 'Sandbox Report': return (
                (sandboxReport || []).map((s, i) =>
                    <React.Fragment key={`${s.type}_${i}`}>
                        <Box p={1} display="flex" style={{ paddingBottom: 0 }}>
                            <Box p={1} flexGrow={1}>
                                <Typography variant="h6">{s.type}</Typography>
                            </Box>
                            <Box p={1} xs={2}>
                                {_get(s, 'data.task_id') &&
                                    <Button variant="outlined"
                                        size="small"
                                        aria-label="Download"
                                        onClick={() => handleDownloadSandboxReport(s)}
                                        startIcon={<SaveAltIcon fontSize={'inherit'} />}>
                                        Download
                                    </Button>
                                }
                            </Box>
                        </Box>
                        <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                            <ul>
                                {Object.entries(Flatten({ object: s, delimiter: '.' }))
                                    .filter(([k]) => !skip(k)).map(([k, v], i) =>
                                        <li key={`${k}_${i}`}>
                                            <KeyValuePair
                                                fileHash={isFileHash(k)}
                                                path={k}
                                                data={v}/>
                                        </li>
                                    )}
                            </ul>
                        </Box>
                    </React.Fragment>
                )
            );
            case 'ML Model Results': return (
                <Box p={1} style={{ height: cheight + 50, minHeight: cheight, width: 'auto' }}>
                    <MLResults
                        data={ _get(props.alert, 'data.ml_server')}
                        height={cheight + 50}
                        fileSize={ _get(props.alert, 'data.bro.bytes_analyzed')}
                        fileDescription={ _get(props.alert, 'data.bro.file_description')}
                        fileMd5={_get(props.alert, 'data.bro.md5') }/>
                </Box>
            );
            case 'Raw JSON': return (
                <React.Fragment>
                    <Box p={1} display="flex" style={{ paddingBottom: 0 }} flexDirection="row-reverse">
                        <Box p={1}>
                            <Button variant="outlined"
                                size="small"
                                aria-label="Save"
                                title="Save to file"
                                onClick={handleSaveJson}
                                startIcon={<SaveAltIcon fontSize={'inherit'} />}>
                                Save
                            </Button>
                        </Box>
                        <Box p={1}>
                            <Button variant="outlined"
                                size="small"
                                aria-label={jsonFormatted ? 'Raw' : 'Formatted'}
                                onClick={handleJSONFormat}>
                                {jsonFormatted ? 'Raw' : 'Formatted'}
                            </Button>
                        </Box>
                    </Box>
                    <Box p={1} display="flex" className={classes.autoColumnList}>
                        {jsonFormatted && <ReactJSON
                            src={{ alert: props.alert, rule, metadata, sandboxReport }}
                            enableClipboard={false}
                            style={{ fontSize: '15px' }}
                            theme={theme.JsonViewerTheme}/>}
                        {!jsonFormatted && <pre
                            className={classes.rawJson}
                            style={{ fontSize: '15px' }}>
                            {JSON.stringify({ alert: props.alert, rule, metadata, sandboxReport }, null, 4)}
                        </pre>}
                    </Box>
                </React.Fragment>
            );
        }
    };

    // profile hook
    useEffect(() => {
        const profile = getProfile('alerts');
        setShowFavorites(_get(profile, 'favorites.show') || false);
        setFavorites(_get(profile, 'favorites.selections') || {});
        const f = Object.values(AlertsService.fields)
            .reduce((a, c) => ({ ...a, [`${c.field}`]: c }), {});
        setFields(f);
    }, [getProfile]);

    // size hook
    useEffect(() => {
        const top = 210;
        setCHeight(Math.max(height - props.header - top, 82));
    }, [height, props.header]);

    useEffect(() => {
        if (!props.alert) return;

        // Reset metadata loaded state when alert changes
        setMetadataLoaded(false);

        const readAlert = () => {
            disambiguate({ object: props.alert, delimiter: '.' });
            const flat = Flatten({ object: props.alert, delimiter: '.' });
            setFlatAlert(Flatten({ object: props.alert, delimiter: '.' }));
            setNote(flat['data.bricata.note'] || '');
            setIsread(true);
            // See if a cylance or ml_server malware alert.
            const m = _get(props.alert, 'data.cylance') || _get(props.alert, 'data.ml_server');
            if (m) {
                const flat = Flatten({ object: { m }, delimiter: '.' });
                setMalware(flat);
                fetchSandboxReport();
            } else {
                setMalware();
            }

            fetchBacktestingJob();

            setTabs(current => _merge(current, {
                Malware: { show: !!m },
                Packet: { show: !!flat['data.packet'] },
                Payload: { show: !!flat['data.payload'] },
                'ML Model Results': { show: !!flat['data.ml_server.Score'] },
                Backtesting: { show: !!GetBTUUID(props.alert, 'data.bt_uuid') },
            }));
        };

        const fetchBacktesting = async () => {
            const b = GetBTUUID(props.alert, 'data.bt_uuid');
            if (b) {
                if (backtestingJobs[b]) {
                    console.log(backtestingJobs[b]);
                    setBacktesting({
                        ...Flatten({ object: backtestingJobs[b], delimiter: '.' }),
                    });
                }
            }
            setTabs(current => ({ ...current, Backtesting: { show: !!b } }));
        };

        const fetchRule = async () => {
            const sig = _get(props.alert, 'data.alert.signature_id');
            const rev = _get(props.alert, 'data.alert.rev');
            if (suriPerms.read) {
                SuricataService.getRuleDetails(sig, rev).then(d => {
                    setRule(d);
                    if (d) {
                        const flat = Flatten({ object: { rule: d }, delimiter: '.' });
                        setRule(flat);
                    } else {
                        setRule();
                    }
                    setTabs(current => ({ ...current, Rule: { show: !!d } }));
                }).catch(() => {
                    setTabs(current => ({ ...current, Rule: { show: false } }));
                });
            }
        };

        const fetchSandboxReport = async () => {
            const d = await AlertsService.getSandboxReport(props.alert.uuid, props.cmc);
            if ((d || []).length > 0) {
                setSandboxReport(d);
            } else {
                setSandboxReport();
            }
            setTabs(current => ({ ...current, 'Sandbox Report': { show: (d || []).length > 0 } }));
        };

        const fetchBacktestingJob = async () => {
            const btuuid = GetBTUUID(props.alert, 'data.bt_uuid');
            if (!btuuid) return;

            const d = await BacktestingService.getJob(backtestingJobs[btuuid].name);
            if (!_isEmpty(d)) {
                const flat = Flatten({
                    object: {
                        backtesting: {
                            ...d,
                        }
                    },
                    delimiter: '.'
                });
                setBacktesting(flat);
            } else {
                setBacktesting();
            }
        };

        const fetchPolicies = async () => {
            if (!suriPerms.read || !gatorPerms.read) return;

            // defense against the dark arts of ipv6
            const sensor = sensorNames[_get(props.alert, 'data.bricata.sensor_uuid')];
            const host = sensor ? sensor.host : _get(props.alert, 'data.bricata.sensor_ipv4');
            const suricy = await PolicyService.getCurrentPolicy('suricata_rules', host, props.cmc);
            const gator = await PolicyService.getCurrentPolicy('data_tags', host, props.cmc);
            setPolicy({ suricy, gator });
        };

        const fetchPcapSituation = async () => {
            const situation = { full: false, smart: false };
            if (!sensorPerms.read || !pcapPerms.read) {
                setPcapSituation(situation);
                return;
            }

            const data = _get(props.alert, 'data');
            if (!data || !data.bricata) {
                setPcapSituation(situation);
                return;
            }
            // is proto compliant?
            const c = PcapService.compliantProtocols.find(p => p === (data.proto || '').toLowerCase());
            if (!c) {
                setPcapSituation(situation);
                return;
            }
            // is pcap running on the sensor?
            const a = await SensorsService.getApps(data.bricata.sensor_uuid, props.cmc);
            // setApps(a || {});
            if (!(a || {}).captured) {
                setPcapSituation(situation);
                return;
            }
            // is pcap and/or smart pcap available for the alert time
            const d = await SensorsService.getPcapStats(data.bricata.sensor_uuid, props.cmc);
            if (!d) {
                setPcapSituation(situation);
                return;
            }
            // pre-3.4 sensor - fallback to just showing the button
            situation.full = d.available;
            const date = Moment(data.timestamp);
            if (_get(d, 'smart.found', 0) > 0) {
                const latest = Moment(d.smart.latest);
                const oldest = Moment(d.smart.oldest);
                situation.smart = Moment(date).isBetween(oldest, latest, '[]');
            }
            if (_get(d, 'full.found', 0) > 0) {
                const latest = Moment(d.full.latest);
                const oldest = Moment(d.full.oldest);
                situation.full = Moment(date).isBetween(oldest, latest, '[]');
            }
            setPcapSituation(situation);
        };

        const checkMetadataAvailability = async () => {
            const d = await AlertsService.getMetadata(props.alert.uuid, props.alert.timestamp, props.cmc);
            // Just check if data exists to show/hide tabs, don't process it yet
            setTabs(current => ({
                ...current,
                Metadata: { show: !_isEmpty(_get(d, 'conn', {})) },
                DPID: { show: !_isEmpty(_get(d, 'dpid', {})) }
            }));
        };

        readAlert();
        fetchRule();
        fetchBacktesting();
        fetchPolicies();
        fetchPcapSituation();
        checkMetadataAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.alert, props.cmc, sensorNames, backtestingJobs]);

    // Lazy load metadata when Metadata or DPID tab is selected
    useEffect(() => {
        const visibleTabs = Object.keys(tabs || {})
            .filter(t => tabs[t].show)
            .sort((a, b) => tabs[a].order - tabs[b].order);
        
        const currentTabName = visibleTabs[tab];
        
        // Only fetch metadata if we're on Metadata or DPID tab and haven't loaded it yet
        if ((currentTabName === 'Metadata' || currentTabName === 'DPID') && !metadataLoaded) {
            const fetchMetadata = async () => {
                const d = await AlertsService.getMetadata(props.alert.uuid, props.alert.timestamp, props.cmc);
                if (!_isEmpty(d)) {
                    // Flatten conn and dpid separately for display
                    const flatConn = Flatten({ object: { conn: _get(d, 'conn', {}) }, delimiter: '.' });
                    const flatDpid = Flatten({ object: { dpid: _get(d, 'dpid', {}) }, delimiter: '.' });
                    setMetadata(flatConn);
                    setDpid(flatDpid);
                } else {
                    setMetadata();
                    setDpid();
                }
                setTabs(current => ({ ...current, Metadata: { show: !_isEmpty(_get(d, 'conn', {})) }, DPID: { show: !_isEmpty(_get(d, 'dpid', {})) } } ));
                setMetadataLoaded(true);
            };
            
            fetchMetadata();
        }
    }, [tab, tabs, metadataLoaded, props.alert, props.cmc]);


    return (
        <Card className={classes.root}>
            <div ref={toolbarRef}/>
            <Box p={2} className={classes.alertHeader}>
                <Box style={{ minWidth: 250 }}>
                    <Typography variant="h5">Alert Details</Typography>
                    {MC2CHECK && props.cmc &&
                        <span>CMC: <span
                            style={{
                                paddingRight: 10,
                                color: theme.palette.secondary.main
                            }}>{props.cmc.name}
                        </span> </span>
                    }
                    Sensor:
                    {props.onFilter && <Link
                        color="primary"
                        onClick={(e) => handleOnValueClick(e.currentTarget, 'data.bricata.sensor_uuid', flatAlert['data.bricata.sensor_uuid'])}
                        style={{ paddingLeft: 10, cursor: 'pointer' }}>
                        {<SensorName uuid={'' + flatAlert['data.bricata.sensor_uuid']} />}
                    </Link>}
                    {!props.onFilter && <span style={{ paddingLeft: 10 }}>
                        {<SensorName uuid={'' + flatAlert['data.bricata.sensor_uuid']} />}
                    </span>}
                </Box>

                <Box style={{ width: 120, minWidth: 120 }}>
                    <Fab
                        title="previous"
                        disabled={!props.onPrev}
                        onClick={props.onPrev}
                        size="small"
                        color={props.isBT ? 'backtesting' : 'primary'}
                        aria-label="previous"
                        className={classes.margin}>
                        <NavigateBeforeIcon/>
                    </Fab>
                    <Fab
                        title="next"
                        disabled={!props.onNext}
                        onClick={props.onNext}
                        size="small"
                        color={props.isBT ? 'backtesting' : 'primary'}
                        aria-label="next"
                        className={classes.margin}>
                        <NavigateNextIcon/>
                    </Fab>
                </Box>

                {/*these go in backwards so we can show ellipsis on the left*/}
                <Box flexGrow={1} className={classes.tagsToolbar}>
                    {(flatAlert['data.bricata.tag'] || []).map((t, i) =>
                        <div key={t + i} className={classes.inlineTag}>
                            <Tag
                                tag={t}
                                onUntag={handleUntag}
                                untagLabel={'untag'}/>
                        </div>
                    )}
                </Box>

                <Grid container justifyContent="flex-end" style={{ width: 'auto' }}>
                    <Grid item>
                        <LabeledToolbar>
                            {/* tag */}
                            {(alertPerms.write && tagPerms.read) &&
                                <Button
                                    aria-label="add tag"
                                    title="add tag"
                                    size="small"
                                    startIcon={<BookmarkIcon />}
                                    onClick={() => setShowTags(true)}>
                                    <p>Tags</p>
                                </Button>
                            }
                            {/* read */}
                            {alertPerms.override && <Button
                                aria-label={isread ? 'mark as unread' : 'mark as read'}
                                title={isread ? 'mark as unread' : 'mark as read'}
                                size="small"
                                startIcon={isread ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                onClick={handleMarkRead}>
                                <p>{isread ? 'Unread' : 'Read'}</p>
                            </Button>}
                            {/* pcap */}
                            {pcapPerms.read && (pcapSituation.full || pcapSituation.smart) &&
                                <PopupMenu
                                    tooltip="request pcap"
                                    aria-label="pcap"
                                    items={getPcapMenu()}
                                    onSelect={handlePcap}
                                    button={<Button size="small" startIcon={<FindInPageIcon/>}>
                                        <p>Pcap</p>
                                    </Button>}
                                    placement={'bottom-end'}/>}
                            {/* hunt */}
                            <PopupMenu
                                tooltip="hunt"
                                aria-label="hunt"
                                items={['By Flow UUID', 'By source IP', 'By dest IP', 'By IP pair']}
                                onSelect={handleHunt}
                                button={<Button size="small" startIcon={<HuntIcon/>}>
                                    <p>Hunt</p>
                                </Button>}
                                placement={'bottom-end'}/>
                            {/* tune */}
                            {sensorPerms.read && gatorPerms.write && canPolicy('gator') &&
                                <Button
                                    aria-label="tune"
                                    title="tune"
                                    size="small"
                                    startIcon={<TuneIcon />}
                                    onClick={handleTune}>
                                    <p>Tune</p>
                                </Button>
                            }
                            {/* share */}
                            <Button
                                aria-label="share link"
                                size="small"
                                startIcon={<ShareIcon />}
                                onClick={() => props.onShare({ uuid: flatAlert.uuid })}
                                title="share link">
                                <p>Share</p>
                            </Button>
                        </LabeledToolbar>
                    </Grid>
                </Grid>
            </Box>

            {tagPerms.read && <Box>
                <TagSimpleList
                    show={showTags}
                    placement='bottom-end'
                    onClickAway={() => setShowTags(false)}
                    anchorEl={toolbarRef.current}
                    onSelect={handleAddTag}/>
            </Box>}

            <AppBar
                position="static"
                color={props.isBT ? 'backtesting' : 'primary'}
                enableColorOnDark={props.isBT}>
                <Tabs
                    value={+tab}
                    onChange={handleTabChanged}
                    indicatorColor='primary'
                    textColor={props.isBT ? 'inherit' : 'primary'}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{
                        style: {
                            background: props.isBT ? theme.palette.backtesting.light : '',
                        }
                    }}>
                    {Object.entries(tabs || {})
                        .filter(([k, v]) => v.show)
                        .sort((a, b) => a[1].order - b[1].order)
                        .map(([k, v], i) =>
                            <Tab key={`${k}_${i}`} label={k}/>
                        )
                    }
                </Tabs>
            </AppBar>
            {Object.entries(tabs || {})
                .filter(([k, v]) => v.show)
                .sort((a, b) => a[1].order - b[1].order)
                .map(([k, v], i) =>
                    <TabPanel key={`${k}_${i}`} current={tab} value={getTabIndex(k)}>
                        {renderTab(k)}
                    </TabPanel>
                )
            }
            <Popover
                open={Boolean(valuePopup)}
                anchorEl={valuePopup ? valuePopup.elm : null}
                onClose={() => setValuePopup()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}>
                <StyledPaper className={classes.paper}>
                    <MenuItem onClick={props.onFilter ? handleOnFilter : undefined}>
                        <ListItemIcon className={classes.itemIcon}>
                            <SearchIcon fontSize='small'/>
                        </ListItemIcon>
                        Filter
                    </MenuItem>
                </StyledPaper>
            </Popover>
            <ConfirmationDialog
                onClose={() => setPartialDownloadConfirm(false)}
                onOK={doDownloadMalware}
                open={partialDownloadConfirm}
                message={'Malware file is incomplete. Download anyway?'}/>
        </Card>
    );
};

AlertDetail.propTypes = {
    alert: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    onSaveNote: PropTypes.func,
    onTag: PropTypes.func,
    onUntag: PropTypes.func,
    onReadAlert: PropTypes.func,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    onFilter: PropTypes.func,
    onTune: PropTypes.func,
    onShare: PropTypes.func,
    header: PropTypes.number,
    cmc: PropTypes.object,
    isBT: PropTypes.bool,
};

export default AlertDetail;
